import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createComponentLogger } from '../utils/logger';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const logger = createComponentLogger('ThemeContext');

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [theme, setThemeState] = useState<'light' | 'dark'>('light');

  // Load theme from user settings or localStorage
  useEffect(() => {
    const loadTheme = async () => {
      // First check localStorage for immediate theme application
      const localTheme = localStorage.getItem('app-theme') as 'light' | 'dark';
      if (localTheme) {
        setThemeState(localTheme);
        document.documentElement.setAttribute('data-theme', localTheme);
      }

      // If user is logged in, load their saved preference
      if (currentUser) {
        try {
          const settingsDoc = await getDoc(doc(db.value, 'userSettings', currentUser.uid));
          if (settingsDoc.exists()) {
            const data = settingsDoc.data();
            if (data.theme) {
              setThemeState(data.theme);
              localStorage.setItem('app-theme', data.theme);
              document.documentElement.setAttribute('data-theme', data.theme);
            }
          }
        } catch (error) {
          logger.error('Error loading theme:', error);
        }
      }
    };

    loadTheme();
  }, [currentUser]);

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  const setTheme = async (newTheme: 'light' | 'dark') => {
    setThemeState(newTheme);
    
    // Save to Firebase if user is logged in
    if (currentUser) {
      try {
        await setDoc(doc(db.value, 'userSettings', currentUser.uid), {
          theme: newTheme
        }, { merge: true });
      } catch (error) {
        logger.error('Error saving theme:', error);
      }
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};